import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const emptyForm = () => ({
  nombre: '', tipo: '', fecha_inicio: new Date().toISOString().split('T')[0],
  fecha_vencimiento: '', estado: 'pendiente',
  parte_contraria: '', resultado: '', notas: '',
  expediente_id: '', cliente_ids: [],
  mostrar_tipo: true, mostrar_parte_contraria: false,
});

export default function TramiteNuevoModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [dropdownClienteOpen, setDropdownClienteOpen] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [cli, exp] = await Promise.all([
        supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('apellido'),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
      ]);
      setClientes(cli.data || []);
      setExpedientes(exp.data || []);
    })();
    setForm(emptyForm());
    setBusquedaCliente('');
  }, [open]);

  function toggleCliente(id) {
    setForm(f => ({
      ...f,
      cliente_ids: f.cliente_ids.includes(id) ? f.cliente_ids.filter(c => c !== id) : [...f.cliente_ids, id],
    }));
  }

  async function saveTramite() {
    if (guardando) return;
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardando(true);
    const tramiteData = {
      nombre: form.nombre,
      tipo: form.mostrar_tipo ? form.tipo : null,
      fecha_inicio: form.fecha_inicio || null,
      fecha_vencimiento: form.fecha_vencimiento || null,
      estado: form.estado,
      parte_contraria: form.mostrar_parte_contraria ? form.parte_contraria : null,
      resultado: form.estado === 'completado' ? form.resultado : null,
      expediente_id: form.expediente_id || null,
    };
    const result = await supabase.from('tramites').insert(tramiteData).select('id').single();
    if (!result.data || result.error) { setGuardando(false); showToast('Error al guardar'); return; }
    const tramiteId = result.data.id;
    if (form.cliente_ids.length > 0) {
      await supabase.from('tramites_clientes').insert(form.cliente_ids.map(cid => ({ tramite_id: tramiteId, cliente_id: cid })));
    }
    setGuardando(false);
    showToast('Trámite creado ✓');
    onSaved?.();
    onClose();
  }

  function handleClose() { setForm(emptyForm()); onClose(); }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && handleClose()}>
        <div className="modal" style={{ width: 620 }}>
          <div className="modal-header">
            <div className="modal-title">Nuevo trámite extrajudicial</div>
            <button className="modal-close" onClick={handleClose}>✕</button>
          </div>
          <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Nombre *</label>
                <input className="form-input" autoFocus value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Carta documento, Pedido de informes" />
              </div>
              <div className="form-group form-full">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>Tipo de trámite</label>
                  <span style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }} onClick={() => setForm(f => ({ ...f, mostrar_tipo: !f.mostrar_tipo, tipo: '' }))}>
                    {form.mostrar_tipo ? 'Quitar' : 'Agregar'}
                  </span>
                </div>
                {form.mostrar_tipo && <input className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} placeholder="Ej: Epistolar, Administrativo" />}
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de inicio</label>
                <input className="form-input" type="date" value={form.fecha_inicio} onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de vencimiento</label>
                <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                  <option value="pendiente">Pendiente</option>
                  <option value="en espera">En espera</option>
                  <option value="completado">Completado</option>
                  <option value="archivado">Archivado</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Expediente vinculado</label>
                <select className="form-select" value={form.expediente_id} onChange={e => setForm(f => ({ ...f, expediente_id: e.target.value }))}>
                  <option value="">Sin vincular</option>
                  {expedientes.map(e => <option key={e.id} value={e.id}>{e.numero ? `${e.numero} — ` : ''}{e.caratula?.substring(0, 35)}</option>)}
                </select>
              </div>
              <div className="form-group form-full">
                <label className="form-label">Clientes vinculados</label>
                {form.cliente_ids.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {form.cliente_ids.map(id => {
                      const c = clientes.find(x => x.id === id);
                      return c ? (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--blue-light)', border: '1px solid var(--blue)', color: 'var(--blue)', fontSize: 12 }}>
                          {c.nombre} {c.apellido}
                          <span onClick={() => toggleCliente(id)} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 2 }}>×</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                <div style={{ position: 'relative' }}>
                  <input className="form-input" placeholder="Buscar cliente por nombre..." value={busquedaCliente}
                    onChange={e => setBusquedaCliente(e.target.value)}
                    onFocus={() => setDropdownClienteOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownClienteOpen(false), 150)}
                    autoComplete="off" />
                  {dropdownClienteOpen && (
                    <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.3)' }}>
                      {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t); }).map(c => (
                        <div key={c.id} onMouseDown={() => { toggleCliente(c.id); setBusquedaCliente(''); }}
                          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: form.cliente_ids.includes(c.id) ? 'var(--blue-light)' : 'transparent', color: form.cliente_ids.includes(c.id) ? 'var(--blue)' : 'var(--text2)' }}>
                          <span>{c.nombre} {c.apellido}</span>
                          {form.cliente_ids.includes(c.id) && <span>✓</span>}
                        </div>
                      ))}
                      {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t); }).length === 0 && (
                        <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={handleClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveTramite} disabled={guardando}>{guardando ? 'Guardando...' : 'Crear trámite'}</button>
          </div>
        </div>
      </div>
      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
